/**
 * Reports Page - Riportok
 *
 * Overview, Sales, Rentals, Inventory, and Financial tabs.
 * Wired to real API endpoints via TanStack Query.
 *
 * API endpoints used:
 * - GET /api/v1/reports/rentals — rental report summary
 * - GET /api/v1/accounting/invoices — invoice listing
 * - GET /api/v1/accounting/vat-summary — VAT summary
 * - GET /api/v1/dashboard/inventory/summary — stock summary
 * - GET /api/v1/dashboard/kpi/revenue — revenue KPI
 */

import { api } from '@/api/client';
import { getMarginReport, getMarginReportExportUrl, type MarginReportItem } from '@/api/products';
import { useTranslation } from '@kgc/i18n';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

type ReportType = 'overview' | 'sales' | 'rentals' | 'inventory' | 'financial' | 'margin';
type DateRange = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

// ============================================
// API Types
// ============================================

interface RentalReportResponse {
  summary: {
    totalRentals: number;
    activeRentals: number;
    closedRentals: number;
    averageRentalDays: number;
    averageRevenuePerRental: number;
    overdueReturns: number;
  };
  byEquipmentType: Array<{
    type: string;
    count: number;
    revenue: number;
  }>;
  periodStart: string;
  periodEnd: string;
}

interface InvoiceListResponse {
  invoices: Array<{
    id: string;
    invoiceNumber: string;
    issueDate: string;
    partnerName: string;
    netAmount: number;
    vatAmount: number;
    grossAmount: number;
    vatRate: number;
    status: string;
  }>;
  totals: {
    totalNet: number;
    totalVat: number;
    totalGross: number;
    count: number;
  };
  query: {
    from: string;
    to: string;
  };
}

interface StockSummaryResponse {
  data: {
    total: number;
    byLocation: Record<string, { count: number; percentage: number }>;
    byStatus: {
      available: number;
      rented: number;
      service: number;
    };
  };
}

interface VatSummaryApiResponse {
  tenantId: string;
  month: string;
  breakdown: Array<{
    vatRate: number;
    netAmount: number;
    vatAmount: number;
    grossAmount: number;
    invoiceCount: number;
  }>;
  totals: {
    totalNet: number;
    totalVat: number;
    totalGross: number;
  };
}

interface KpiResponseDto {
  value: number;
  previousValue: number;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
  breakdown?: Array<{
    label: string;
    value: number;
  }>;
}

// ============================================
// Date Range Helpers
// ============================================

function getDateRange(range: DateRange): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().split('T')[0] ?? now.toISOString().slice(0, 10);
  let fromDate: Date;

  switch (range) {
    case 'today':
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'week':
      fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case 'month':
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'quarter': {
      const q = Math.floor(now.getMonth() / 3) * 3;
      fromDate = new Date(now.getFullYear(), q, 1);
      break;
    }
    case 'year':
      fromDate = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
  }

  const from = fromDate.toISOString().split('T')[0] ?? fromDate.toISOString().slice(0, 10);
  return { from, to };
}

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// ============================================
// API Fetchers
// ============================================

async function fetchRentalReport(from: string, to: string): Promise<RentalReportResponse> {
  return api.get<RentalReportResponse>(`/reports/rentals?from=${from}&to=${to}`);
}

async function fetchInvoices(from: string, to: string): Promise<InvoiceListResponse> {
  return api.get<InvoiceListResponse>(`/accounting/invoices?from=${from}&to=${to}`);
}

async function fetchInventorySummary(): Promise<StockSummaryResponse> {
  return api.get<StockSummaryResponse>('/dashboard/inventory/summary');
}

async function fetchVatSummary(month: string): Promise<VatSummaryApiResponse> {
  return api.get<VatSummaryApiResponse>(`/accounting/vat-summary?month=${month}`);
}

async function fetchRevenueKpi(from: string, to: string): Promise<KpiResponseDto> {
  return api.get<KpiResponseDto>(
    `/dashboard/kpi/revenue?dateFrom=${from}T00:00:00Z&dateTo=${to}T23:59:59Z&comparison=true`
  );
}

// ============================================
// Loading Skeleton Component
// ============================================

function LoadingSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
      <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
    </div>
  );
}

function ErrorState({ error }: { error: Error | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16">
      <AlertTriangle className="h-6 w-6 text-destructive" />
      <p className="text-destructive">{t('report.errorLoading')}</p>
      <p className="text-sm text-muted-foreground">{error?.message ?? ''}</p>
    </div>
  );
}

function formatHuf(amount: number): string {
  return `${amount.toLocaleString('hu-HU')} Ft`;
}

// ============================================
// Component
// ============================================

export function ReportsPage() {
  const { t } = useTranslation();
  const [activeReport, setActiveReport] = useState<ReportType>('overview');
  const [dateRange, setDateRange] = useState<DateRange>('month');

  const { from, to } = useMemo(() => getDateRange(dateRange), [dateRange]);

  // Rental report — used by overview and rentals tabs
  const rentalQuery = useQuery({
    queryKey: ['reports-rental', from, to],
    queryFn: () => fetchRentalReport(from, to),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: activeReport === 'overview' || activeReport === 'rentals',
  });

  // Invoices — used by overview and sales tabs
  const invoicesQuery = useQuery({
    queryKey: ['reports-invoices', from, to],
    queryFn: () => fetchInvoices(from, to),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: activeReport === 'overview' || activeReport === 'sales',
  });

  // Revenue KPI — used by overview and financial tabs
  const revenueQuery = useQuery({
    queryKey: ['reports-revenue-kpi', from, to],
    queryFn: () => fetchRevenueKpi(from, to),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: activeReport === 'overview' || activeReport === 'financial',
  });

  // Inventory summary — used by inventory tab
  const inventoryQuery = useQuery({
    queryKey: ['reports-inventory-summary'],
    queryFn: fetchInventorySummary,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: activeReport === 'inventory',
  });

  // VAT summary — used by financial tab
  const vatQuery = useQuery({
    queryKey: ['reports-vat', getCurrentMonth()],
    queryFn: () => fetchVatSummary(getCurrentMonth()),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: activeReport === 'financial',
  });

  // Margin report — SALES-FG-4-11
  const [marginGroupBy, setMarginGroupBy] = useState<'product' | 'category'>('product');
  const marginQuery = useQuery({
    queryKey: ['reports-margin', from, to, marginGroupBy],
    queryFn: () => getMarginReport({ from, to, groupBy: marginGroupBy }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: activeReport === 'margin',
  });

  const reportTabs = [
    {
      id: 'overview' as ReportType,
      label: t('report.overview'),
      icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z',
    },
    {
      id: 'sales' as ReportType,
      label: t('report.sales'),
      icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
    },
    {
      id: 'rentals' as ReportType,
      label: t('report.rentals'),
      icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    },
    {
      id: 'inventory' as ReportType,
      label: t('report.inventory'),
      icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    },
    {
      id: 'financial' as ReportType,
      label: t('report.financial'),
      icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z',
    },
    {
      id: 'margin' as ReportType,
      label: t('report.margin.title'),
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    },
  ];

  const dateRangeOptions = [
    { id: 'today' as DateRange, label: t('report.today') },
    { id: 'week' as DateRange, label: t('report.week') },
    { id: 'month' as DateRange, label: t('report.month') },
    { id: 'quarter' as DateRange, label: t('report.quarter') },
    { id: 'year' as DateRange, label: t('report.year') },
  ];

  // =======================
  // Overview Tab
  // =======================
  const renderOverview = () => {
    const isLoading = rentalQuery.isLoading || invoicesQuery.isLoading || revenueQuery.isLoading;
    const isError = rentalQuery.isError || invoicesQuery.isError || revenueQuery.isError;

    if (isLoading) return <LoadingSkeleton />;
    if (isError)
      return <ErrorState error={rentalQuery.error ?? invoicesQuery.error ?? revenueQuery.error} />;

    const revenueValue = revenueQuery.data?.value ?? 0;
    const revenueChange = revenueQuery.data?.changePercent ?? 0;
    const activeRentals = rentalQuery.data?.summary.activeRentals ?? 0;
    const salesCount = invoicesQuery.data?.totals.count ?? 0;
    const totalRentals = rentalQuery.data?.summary.totalRentals ?? 0;

    const overviewCards = [
      {
        title: t('report.totalRevenue'),
        value: formatHuf(revenueValue),
        change: revenueChange,
        changeLabel: t('report.comparedToPrevMonth'),
        icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      },
      {
        title: t('dashboard.activeRentals'),
        value: String(activeRentals),
        change: 0,
        changeLabel: t('report.comparedToPrevMonth'),
        icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
      },
      {
        title: t('report.salesCount'),
        value: String(salesCount),
        change: 0,
        changeLabel: t('report.comparedToPrevMonth'),
        icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
      },
      {
        title: t('report.rentals'),
        value: String(totalRentals),
        change: 0,
        changeLabel: t('report.comparedToPrevMonth'),
        icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
      },
    ];

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {overviewCards.map(card => (
            <div
              key={card.title}
              className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <div className="rounded-lg bg-primary/10 p-2">
                  <svg
                    className="h-6 w-6 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={card.icon}
                    />
                  </svg>
                </div>
                {card.change !== 0 && (
                  <span
                    className={`flex items-center text-sm font-medium ${card.change >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {card.change >= 0 ? '+' : ''}
                    {card.change.toFixed(1)}%
                    <svg
                      className={`ml-1 h-4 w-4 ${card.change < 0 ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 10l7-7m0 0l7 7m-7-7v18"
                      />
                    </svg>
                  </span>
                )}
              </div>
              <div className="mt-4">
                <h3 className="text-2xl font-bold text-foreground">{card.value}</h3>
                <p className="text-sm text-muted-foreground">{card.title}</p>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{card.changeLabel}</p>
            </div>
          ))}
        </div>

        {/* Charts placeholder */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
            <h3 className="mb-4 text-lg font-semibold text-foreground">
              {t('report.revenueTrend')}
            </h3>
            <div className="flex h-64 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
              <div className="text-center text-muted-foreground">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                  />
                </svg>
                <p className="mt-2">{t('report.revenueChart')}</p>
              </div>
            </div>
          </div>

          <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
            <h3 className="mb-4 text-lg font-semibold text-foreground">
              {t('report.categoryDistribution')}
            </h3>
            <div className="flex h-64 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
              <div className="text-center text-muted-foreground">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                  />
                </svg>
                <p className="mt-2">{t('report.categoryChart')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // =======================
  // Sales Tab
  // =======================
  const renderSalesReport = () => {
    if (invoicesQuery.isLoading) return <LoadingSkeleton />;
    if (invoicesQuery.isError) return <ErrorState error={invoicesQuery.error} />;

    const invoiceData = invoicesQuery.data;
    const invoices = invoiceData?.invoices ?? [];
    const totals = invoiceData?.totals ?? { totalNet: 0, totalVat: 0, totalGross: 0, count: 0 };

    // Group invoices by VAT rate for a category-like view
    const byVatRate = new Map<number, { rate: number; amount: number; count: number }>();
    for (const inv of invoices) {
      const existing = byVatRate.get(inv.vatRate);
      if (existing) {
        existing.amount += inv.grossAmount;
        existing.count += 1;
      } else {
        byVatRate.set(inv.vatRate, { rate: inv.vatRate, amount: inv.grossAmount, count: 1 });
      }
    }
    const salesCategories = Array.from(byVatRate.values()).sort((a, b) => b.amount - a.amount);
    const totalAmount = salesCategories.reduce((sum, s) => sum + s.amount, 0);

    return (
      <div className="space-y-6">
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            {t('report.salesByCategory')}
          </h3>
          {invoices.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">{t('report.noDataForFilters')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.category')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.amount')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.pieces')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.avgPerPiece')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.ratio')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {salesCategories.map(item => {
                    const percentage =
                      totalAmount > 0 ? ((item.amount / totalAmount) * 100).toFixed(1) : '0.0';
                    return (
                      <tr key={item.rate} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-foreground">
                          {item.rate}% AFA
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                          {formatHuf(item.amount)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                          {item.count}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                          {item.count > 0 ? formatHuf(Math.round(item.amount / item.count)) : '-'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                          <div className="flex items-center justify-end gap-2">
                            <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                              <div
                                className="h-full rounded-full bg-primary"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span>{percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-foreground">
                      {t('report.total')}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-foreground">
                      {formatHuf(totals.totalGross)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-foreground">
                      {totals.count}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-foreground">
                      -
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-foreground">
                      100%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Sales chart placeholder */}
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
          <h3 className="mb-4 text-lg font-semibold text-foreground">{t('report.dailySales')}</h3>
          <div className="flex h-64 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
            <div className="text-center text-muted-foreground">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <p className="mt-2">{t('report.dailySalesChart')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // =======================
  // Rentals Tab
  // =======================
  const renderRentalsReport = () => {
    if (rentalQuery.isLoading) return <LoadingSkeleton />;
    if (rentalQuery.isError) return <ErrorState error={rentalQuery.error} />;

    const data = rentalQuery.data;
    const summary = data?.summary;
    const byEquipment = data?.byEquipmentType ?? [];

    return (
      <div className="space-y-6">
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
          <h3 className="mb-4 text-lg font-semibold text-foreground">{t('report.topRentals')}</h3>
          {byEquipment.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">{t('report.noDataForFilters')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.equipment')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.rentalsCount')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.revenue')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {byEquipment.map(item => (
                    <tr key={item.type} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-foreground">
                        {item.type}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                        {item.count}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                        {formatHuf(item.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Rental metrics */}
        {summary && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
              <div className="text-sm text-muted-foreground">{t('report.avgRentalDuration')}</div>
              <div className="mt-1 text-2xl font-bold text-foreground">
                {t('report.avgRentalDays', { days: summary.averageRentalDays.toFixed(1) })}
              </div>
            </div>
            <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
              <div className="text-sm text-muted-foreground">{t('report.overdueReturns')}</div>
              <div className="mt-1 text-2xl font-bold text-red-600">{summary.overdueReturns}</div>
            </div>
            <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
              <div className="text-sm text-muted-foreground">{t('report.avgDeposit')}</div>
              <div className="mt-1 text-2xl font-bold text-foreground">
                {formatHuf(summary.averageRevenuePerRental)}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // =======================
  // Inventory Tab
  // =======================
  const renderInventoryReport = () => {
    if (inventoryQuery.isLoading) return <LoadingSkeleton />;
    if (inventoryQuery.isError) return <ErrorState error={inventoryQuery.error} />;

    const summaryData = inventoryQuery.data?.data;
    if (!summaryData) {
      return (
        <p className="py-8 text-center text-muted-foreground">{t('report.noDataForFilters')}</p>
      );
    }

    const { total, byLocation, byStatus } = summaryData;
    const locationEntries = Object.entries(byLocation);

    return (
      <div className="space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('report.inStock')}</div>
            <div className="mt-1 text-2xl font-bold text-foreground">{total}</div>
          </div>
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('report.available')}</div>
            <div className="mt-1 text-2xl font-bold text-green-600">{byStatus.available}</div>
          </div>
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('report.reserved')}</div>
            <div className="mt-1 text-2xl font-bold text-foreground">{byStatus.rented}</div>
          </div>
        </div>

        {/* Location breakdown table */}
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            {t('report.inventoryByCategory')}
          </h3>
          {locationEntries.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">{t('report.noDataForFilters')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.category')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.inStock')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.ratio')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {locationEntries.map(([location, data]) => (
                    <tr key={location} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-foreground">
                        {location}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                        {data.count}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                        <div className="flex items-center justify-end gap-2">
                          <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div
                              className="h-full rounded-full bg-primary"
                              style={{ width: `${data.percentage}%` }}
                            />
                          </div>
                          <span>{data.percentage.toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-foreground">
                      {t('report.total')}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-foreground">
                      {total}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-foreground">
                      100%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Status breakdown */}
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
          <h3 className="mb-4 text-lg font-semibold text-foreground">{t('report.utilization')}</h3>
          <div className="space-y-3">
            {[
              { label: t('report.available'), value: byStatus.available, color: 'bg-green-500' },
              { label: t('report.reserved'), value: byStatus.rented, color: 'bg-blue-500' },
              { label: t('report.lowStock'), value: byStatus.service, color: 'bg-yellow-500' },
            ].map(item => (
              <div key={item.label}>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="font-medium text-foreground">{item.value}</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className={`h-full rounded-full ${item.color}`}
                    style={{ width: `${total > 0 ? (item.value / total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // =======================
  // Financial Tab
  // =======================
  const renderFinancialReport = () => {
    const isLoading = vatQuery.isLoading || revenueQuery.isLoading || invoicesQuery.isLoading;
    const isError = vatQuery.isError || revenueQuery.isError;

    if (isLoading) return <LoadingSkeleton />;
    if (isError) return <ErrorState error={vatQuery.error ?? revenueQuery.error} />;

    const revenueValue = revenueQuery.data?.value ?? 0;
    const revenueChange = revenueQuery.data?.changePercent ?? 0;
    const vatTotals = vatQuery.data?.totals ?? { totalNet: 0, totalVat: 0, totalGross: 0 };
    const invoiceTotals = invoicesQuery.data?.totals ?? {
      totalNet: 0,
      totalVat: 0,
      totalGross: 0,
      count: 0,
    };
    const revenueBreakdown = revenueQuery.data?.breakdown ?? [];

    // Calculate net result (revenue - VAT - estimated costs)
    const netResult = revenueValue - vatTotals.totalVat;

    return (
      <div className="space-y-6">
        {/* Financial summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('report.grossRevenue')}</div>
            <div className="mt-1 text-2xl font-bold text-foreground">{formatHuf(revenueValue)}</div>
            {revenueChange !== 0 && (
              <div
                className={`mt-2 text-xs ${revenueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}
              >
                {revenueChange >= 0 ? '+' : ''}
                {revenueChange.toFixed(1)}% {t('report.comparedToPrevMonthShort')}
              </div>
            )}
          </div>
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('report.vatPayment')}</div>
            <div className="mt-1 text-2xl font-bold text-foreground">
              {formatHuf(vatTotals.totalVat)}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">{t('report.vatRate')}</div>
          </div>
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('finance.receivables')}</div>
            <div className="mt-1 text-2xl font-bold text-yellow-600">
              {formatHuf(invoiceTotals.totalGross)}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {t('report.expiredInvoices', { count: invoiceTotals.count })}
            </div>
          </div>
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('report.netResult')}</div>
            <div
              className={`mt-1 text-2xl font-bold ${netResult >= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              {formatHuf(netResult)}
            </div>
          </div>
        </div>

        {/* Revenue breakdown */}
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            {t('report.revenueBreakdown')}
          </h3>
          {revenueBreakdown.length === 0 ? (
            <p className="py-4 text-center text-muted-foreground">{t('report.noDataForFilters')}</p>
          ) : (
            <div className="space-y-4">
              {revenueBreakdown.map(item => {
                const total = revenueBreakdown.reduce((sum, i) => sum + i.value, 0);
                const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
                return (
                  <div key={item.label}>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{item.label}</span>
                      <span className="font-medium text-foreground">{formatHuf(item.value)}</span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="mt-0.5 text-right text-xs text-muted-foreground">
                      {percentage}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // =======================
  // Margin Tab — SALES-FG-4-11
  // =======================
  const renderMarginReport = () => {
    if (marginQuery.isLoading) return <LoadingSkeleton />;
    if (marginQuery.isError) return <ErrorState error={marginQuery.error} />;

    const reportData = marginQuery.data?.data;
    if (!reportData) {
      return (
        <p className="py-8 text-center text-muted-foreground">{t('report.noDataForFilters')}</p>
      );
    }

    const { items, totals } = reportData;

    // QA-142 L1: a `null` = „nincs adat" — semleges szin, nem a 0%-os piros.
    const NO_DATA = '—';
    const fmtHuf = (v: number | null) => (v === null ? NO_DATA : formatHuf(v));
    const fmtPct = (v: number | null) => (v === null ? NO_DATA : `${v.toFixed(1)}%`);
    const getMarginColor = (percent: number | null) => {
      if (percent === null) return 'text-muted-foreground';
      if (percent < 10) return 'text-red-600 dark:text-red-400';
      if (percent < 25) return 'text-yellow-600 dark:text-yellow-400';
      return 'text-green-600 dark:text-green-400';
    };

    const getMarginBg = (percent: number | null) => {
      if (percent === null) return 'bg-gray-100 dark:bg-gray-800';
      if (percent < 10) return 'bg-red-100 dark:bg-red-900/30';
      if (percent < 25) return 'bg-yellow-100 dark:bg-yellow-900/30';
      return 'bg-green-100 dark:bg-green-900/30';
    };

    const exportUrl = getMarginReportExportUrl({ from, to, groupBy: marginGroupBy });

    return (
      <div className="space-y-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
            {(['product', 'category'] as const).map(opt => (
              <button
                key={opt}
                onClick={() => setMarginGroupBy(opt)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  marginGroupBy === opt
                    ? 'bg-white text-foreground shadow-sm dark:bg-gray-700'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt === 'product' ? t('report.margin.byProduct') : t('report.margin.byCategory')}
              </button>
            ))}
          </div>
          <a
            href={exportUrl}
            className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <Download className="h-4 w-4" />
            {t('report.margin.exportCsv')}
          </a>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('report.margin.revenue')}</div>
            <div className="mt-1 text-2xl font-bold text-foreground">
              {formatHuf(totals.revenue)}
            </div>
          </div>
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('report.margin.cogs')}</div>
            <div className="mt-1 text-2xl font-bold text-foreground">{fmtHuf(totals.cogs)}</div>
          </div>
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('report.margin.marginAmount')}</div>
            <div className={`mt-1 text-2xl font-bold ${getMarginColor(totals.marginPercent)}`}>
              {fmtHuf(totals.marginAmount)}
            </div>
          </div>
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">{t('report.margin.marginPercent')}</div>
            <div className={`mt-1 text-2xl font-bold ${getMarginColor(totals.marginPercent)}`}>
              {fmtPct(totals.marginPercent)}
            </div>
          </div>
        </div>

        {/* Data table */}
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            {t('report.margin.detailTable')}
          </h3>
          {items.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">{t('report.noDataForFilters')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {marginGroupBy === 'product'
                        ? t('report.margin.productName')
                        : t('report.category')}
                    </th>
                    {marginGroupBy === 'product' && (
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {t('product.sku')}
                      </th>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.margin.revenue')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.margin.cogs')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.margin.marginAmount')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.margin.marginPercent')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('report.pieces')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {items.map((item: MarginReportItem) => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-foreground">
                        {item.name}
                      </td>
                      {marginGroupBy === 'product' && (
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-mono text-muted-foreground">
                          {item.sku ?? ''}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                        {formatHuf(item.revenue)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                        {fmtHuf(item.cogs)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-foreground">
                        {fmtHuf(item.marginAmount)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getMarginBg(item.marginPercent)} ${getMarginColor(item.marginPercent)}`}
                        >
                          {fmtPct(item.marginPercent)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                        {item.quantitySold}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <td
                      className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-foreground"
                      colSpan={marginGroupBy === 'product' ? 2 : 1}
                    >
                      {t('report.total')}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-foreground">
                      {formatHuf(totals.revenue)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-foreground">
                      {fmtHuf(totals.cogs)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-foreground">
                      {fmtHuf(totals.marginAmount)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-foreground">
                      {fmtPct(totals.marginPercent)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeReport) {
      case 'overview':
        return renderOverview();
      case 'sales':
        return renderSalesReport();
      case 'rentals':
        return renderRentalsReport();
      case 'inventory':
        return renderInventoryReport();
      case 'financial':
        return renderFinancialReport();
      case 'margin':
        return renderMarginReport();
    }
  };

  return (
    <div className="kgc-bg min-h-screen p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('report.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('report.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            {t('common.export')}
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            {t('common.print')}
          </button>
        </div>
      </div>

      {/* Report tabs */}
      <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700">
        {reportTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveReport(tab.id)}
            className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeReport === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
            }`}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
            </svg>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Date range filter */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <span className="text-sm font-medium text-muted-foreground">{t('report.period')}</span>
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
          {dateRangeOptions.map(option => (
            <button
              key={option.id}
              onClick={() => setDateRange(option.id)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                dateRange === option.id
                  ? 'bg-white text-foreground shadow-sm dark:bg-gray-700'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button className="ml-auto inline-flex items-center gap-1 text-sm text-primary hover:underline">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          {t('report.customPeriod')}
        </button>
      </div>

      {/* Report content */}
      {renderContent()}
    </div>
  );
}
